// export default ProjectLayout;d
import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import api from '../config/api';
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Chip, Divider, LinearProgress, Alert, AlertTitle, CircularProgress } from "@mui/material";
import ProjectCard from "./ProjectCard";
import ProjectModal from "./ProjectModal";
import useStore from "../config/store";
import TopBar from "../layouts/TopBar";
import { useTranslation } from 'react-i18next';

const ProjectLayout = () => {
  const [projects, setProjects] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(0);
  const [counter, setCounter] = useState(0);

  // Estados para verificación de límites de uso
  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  const { user, authToken, organizationName, organization } =
    useStore.getState();
  const { showAlert } = useStore();
  const { t } = useTranslation();

  // Verificar límites de uso al cargar el componente
  useEffect(() => {
    if (organization && authToken) {
      checkUsageLimits();
    }
  }, [organization, authToken]);

  const checkUsageLimits = async () => {
    if (!organization || !authToken) return;

    try {
      setLoadingUsageCheck(true);
      const { data } = await api.get(
        `/organizations/${organization}/usage/check?action=projects`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (data.response && data.response.status_response === "OK") {
        setUsageCheck(data.response);
      } else {
        console.error("Error al verificar límites de uso:", data);
        setUsageCheck({ canPerform: true }); // En caso de error, permitir uso
      }
    } catch (error) {
      console.error("Error al verificar límites de uso:", error);
      setUsageCheck({ canPerform: true }); // En caso de error, permitir uso
    } finally {
      setLoadingUsageCheck(false);
    }
  };

  useEffect(() => {
    const getOrganization = async () => {
      try {
        const response = await api.get(
          `/v2/projects/${organization}`,
          {
            headers: { authorization: `${authToken}` },
          }
        );

        if (
          response.data.response.message ===
          "All projects were successfully obtained"
        ) {
          setProjects(response.data.response.data);

          if (response.data.response.data.length === 0) {
            showAlert(t('projects.noProjects'), "info");
          } else {
            // showAlert("Proyectos cargados exitosamente", "info");
          }
        } else {
          setError(true);
          showAlert(t('projects.loadError'), "warning");
        }
        setLoaded(true);
      } catch (err) {
        setError(true);
        showAlert(t('projects.loadError'), "warning");
        console.error(err);
      }
    };

    getOrganization();

    // AGREGAR: Actualizar límites de uso cuando cambia el counter
    if (organization && authToken) {
      checkUsageLimits();
    }
  }, [counter, organization, authToken, showAlert]);

  const handleOpen = (action, position) => {
    setModalType(action);
    setSelectedPosition(position);
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  const gridWidth = (size) => {
    switch (size) {
      case "xs":
        return 3;
      case "sm":
        return 6;
      case "md":
        if (projects?.length === 1) {
          return 12;
        } else {
          return 6;
        }
      default:
        return null;
    }
  };

  const renderUsageLimitMessage = () => {
    if (!usageCheck) return null;

    // AGREGAR: No mostrar para proyectos
    if (usageCheck.action === "projects") return null;

    const limitText = usageCheck.limit === -1 ? t('projects.unlimited') : usageCheck.limit;

    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Alert severity="warning">
          <AlertTitle>{t('projects.usageLimitReached')}</AlertTitle>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('projects.usageLimitMessage')}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('projects.currentUsage')}:</strong> {usageCheck.currentUsage} / {limitText} {t('projects.projects')}
            </Typography>
            {usageCheck.remaining !== undefined && usageCheck.remaining >= 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('projects.remainingProjects')}:</strong> {usageCheck.remaining}
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    );
  };

  const renderCurrentUsageAlert = () => {
    if (!usageCheck || loadingUsageCheck) return null;

    // AGREGAR: No mostrar para proyectos
    if (usageCheck.action === "projects") return null;

    const limitText = usageCheck.limit === -1 ? t('projects.unlimited') : usageCheck.limit;
    const severity = usageCheck.canPerform ? 'info' : 'warning';

    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Alert severity={severity}>
          <Typography variant="body2">
            <strong>{t('projects.currentUsage')}:</strong> {usageCheck.currentUsage} / {limitText} {t('projects.projects')}
          </Typography>
          {usageCheck.remaining !== undefined && usageCheck.remaining >= 0 && usageCheck.canPerform && (
            <Typography variant="body2">
              <strong>{t('projects.remainingProjects')}:</strong> {usageCheck.remaining}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  };

  return (
    <Box>
      <TopBar />
      {!error ? (
        <Box sx={{ pt: "64px" }}>
          <Paper
            sx={{
              p: 1.5,
              pr: 3,
              backgroundColor: "#6A5AF9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "0px !important",
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: "white", ml: 4, fontSize: "1rem" }}
            >
              {t('projects.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: "white", pr: 1 }}>
              {organizationName}
            </Typography>
            <Box>
              {/* <img src={corporativeLogo} alt="Logo" style={{ height: '35px' }} /> */}
            </Box>
          </Paper>
          <Container
            maxWidth="xl"
            sx={{ mt: 2, mb: 4, display: "flex", flexWrap: "wrap" }}
          >
            {/* Mostrar spinner mientras verifica límites */}
            {loadingUsageCheck ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4, width: '100%' }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>{t('projects.checkingUsageLimits')}</Typography>
              </Box>
            ) : (
              <>
                {/* Mostrar un solo alert: uso actual O límite alcanzado */}
                {usageCheck && !usageCheck.canPerform ? (
                  renderUsageLimitMessage()
                ) : (
                  renderCurrentUsageAlert()
                )}

                <Grid
                  container
                  spacing={{ xs: 1, md: 2 }}
                  columns={{ xs: 3, sm: 6, md: 12 }}
                >
                  <Grid item xs={12} sm={12} md={12}>
                    <Divider
                      sx={{ width: "85%", m: "2rem auto", textAlign: "center" }}
                    >
                      <Chip
                        sx={{
                          fontSize: "1.5rem",
                          backgroundColor: "transparent",
                          color: "#575757",
                        }}
                        label={
                          user.rol === "user"
                            ? t('projects.projectSelectUser')
                            : t('projects.projectSelect')
                        }
                      />
                    </Divider>
                    <Typography
                      variant="p"
                      component="p"
                      sx={
                        !loaded
                          ? { mt: 3, color: "#6A5AF9" }
                          : { mt: 3, color: "#6A5AF9" }
                      }
                    >
                      {!loaded ? (
                        t('projects.projectLoading')
                      ) : null}
                    </Typography>
                    {!loaded && (
                      <LinearProgress
                        color="inherit"
                        sx={{ margin: "0 auto", mt: 1 }}
                      />
                    )}
                  </Grid>
                  {loaded ? (
                    projects?.length > 0 ? (
                      projects?.map((project, i) => (
                        <Grid
                          key={i}
                          item
                          xs={gridWidth("xs")}
                          sm={gridWidth("sm")}
                          md={gridWidth("md")}
                        >
                          <ProjectCard
                            key={"p" + i}
                            name={project.name}
                            handleOpen={handleOpen}
                            position={i}
                            projectId={project.uid}
                            imgPath={project.imgPath}
                            role={user.rol}
                            language={project.language}
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12} sm={12} md={12}>
                        <Typography variant="h6" component="h4">
                          {user.rol === "user"
                            ? t('projects.projectCreateUser')
                            : t('projects.projectCreate')}
                        </Typography>
                      </Grid>
                    )
                  ) : null}
                </Grid>
              </>
            )}

            {/* Botón Fab - Solo mostrar si no hay error de límites y es admin */}
            {user.rol !== "user" && !loadingUsageCheck && usageCheck && usageCheck.canPerform && (
              <Fab
                onClick={() => handleOpen("add")}
                color="warning"
                aria-label="add"
                sx={{
                  position: "fixed",
                  bottom: 32,
                  right: 64,
                }}
              >
                <AddIcon />
              </Fab>
            )}
          </Container>
        </Box>
      ) : (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            component="h4"
            sx={{
              color: "red",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            {t('projects.loadError')}
          </Typography>
        </Grid>
      )}

      {openModal && (
        <ProjectModal
          project={projects[selectedPosition] ?? null}
          openModal={openModal}
          handleClose={handleClose}
          type={modalType}
          setCounter={setCounter}
          organizationId={organization}
          authToken={authToken}
          showAlert={showAlert}
        />
      )}
    </Box>
  );
};

export default ProjectLayout;